import type { WebContainer, WebContainerProcess } from '@webcontainer/api';
import type { Terminal } from '@xterm/xterm';
import { withResolvers } from './promises';

interface RunShellOptions {
  signal: AbortSignal;
  env?: Record<string, string | boolean>;
  shouldClose?(): boolean;
}

type OSCHandler = (data: string) => void;
type OutputHandler = (data: string) => void;

export interface Shell {
  ready(): Promise<void>;
  restart(clearTerminal?: boolean): Promise<void>;
  write(input: string): void;
  registerOscHandler(handler: OSCHandler): VoidFunction;
  registerOutputHandler(handler: OutputHandler): VoidFunction;
}

export function runShellInTerminal(
  webcontainer: WebContainer,
  terminal: Terminal,
  { signal, shouldClose, env }: RunShellOptions,
): Shell {
  const abortedBySignal = once(signal, 'abort').then(() => true);

  let writer: WritableStreamDefaultWriter;

  let ready = withResolvers<void>();
  let restarting: PromiseWithResolvers<void> | undefined;

  const oscHandlers = new Set<OSCHandler>();
  const outputHandlers = new Set<(data: string) => void>();

  let process: WebContainerProcess;
  let clearTerminalOnRestart = true;

  (async () => {
    let stop = false;

    while (!stop) {
      clearTerminalOnRestart = true;

      process = await webcontainer.spawn('/bin/jsh', ['--osc'], {
        terminal: {
          cols: terminal.cols,
          rows: terminal.rows,
        },
        env,
      });

      restarting?.resolve();

      writer = process.input.getWriter();

      if (signal.aborted) {
        process.kill();
        return;
      }

      const done = runShellToCompletion(process, writer, terminal, {
        onOSC(data) {
          if (data === 'interactive') {
            // whenever we saw the prompt, we are ready
            ready.resolve();
          }

          for (const handler of oscHandlers) {
            handler(data);
          }
        },
        onOutput: (data) => {
          for (const handler of outputHandlers) {
            handler(data);
          }
        },
      });

      const aborted = await Promise.race([done, abortedBySignal]);

      if (aborted) {
        process.kill();
        return;
      }

      if (clearTerminalOnRestart) {
        terminal.clear();
      }

      stop = shouldClose?.() ?? false;

      if (!stop) {
        // reset whenever the process got killed and we don't want to stop
        const previousReady = ready;

        ready = withResolvers<void>();

        // reject the previous ready so the that the returned `ready()` method starts waiting for the new `ready`
        previousReady.reject();
      }
    }
  })();

  return {
    async ready() {
      /**
       * When the process gets restarted before the previous process was ready, the `ready.promise` gets rejected
       * and we start listening for the new ready promise.
       */
      async function onReady() {
        try {
          return await ready.promise;
        } catch {
          return onReady();
        }
      }

      return onReady();
    },
    async restart(clearTerminal = true) {
      restarting = withResolvers<void>();

      clearTerminalOnRestart = clearTerminal;

      terminal.write('\n\n');

      process.kill();

      // we wait until the shell restarted
      await restarting.promise;
    },
    write(input: string) {
      writer.write(input);
    },
    registerOscHandler(handler) {
      oscHandlers.add(handler);

      return () => {
        oscHandlers.delete(handler);
      };
    },
    registerOutputHandler(handler: OutputHandler) {
      outputHandlers.add(handler);

      return () => {
        outputHandlers.delete(handler);
      };
    },
  };
}

async function runShellToCompletion(
  process: WebContainerProcess,
  input: WritableStreamDefaultWriter<string>,
  terminal: Terminal,
  callbacks?: {
    onOSC?: (data: string) => void;
    onOutput?: (data: string) => void;
  },
) {
  const output = process.output;
  const exit = process.exit;

  let isInteractive = false;

  output.pipeTo(
    new WritableStream({
      write(data) {
        if (!isInteractive || callbacks?.onOSC) {
          const [, osc] = data.match(/\x1b\]654;([^\x07]+)\x07/) || [];

          if (osc === 'interactive') {
            // wait until we see the interactive OSC
            isInteractive = true;
          }

          if (osc) {
            callbacks?.onOSC?.(osc);
          }
        }

        terminal.write(data);
        callbacks?.onOutput?.(data);
      },
    }),
  );

  const resizeDisposable = terminal.onResize((dimensions) => {
    process.resize(dimensions);
  });

  const dataDisposable = terminal.onData((data) => {
    if (isInteractive) {
      input.write(data);
    }
  });

  await exit;

  resizeDisposable.dispose();
  dataDisposable.dispose();
}

function once<T extends Event>(target: EventTarget, event: string) {
  const deferred = withResolvers<T>();

  target.addEventListener(event, deferred.resolve as EventListener, { once: true });

  return deferred.promise as Promise<T>;
}
